import { OpenAI, Session } from '@maximizer/core/shared/domain';
import {
  LangfusePromptClient,
  LangfuseTraceClient,
  LangfuseGenerationClient,
} from 'langfuse';
import { LangFuseService } from './langfuse.service';

export class LangfuseRequestTracker implements OpenAI.RequestTracker {
  constructor(
    private readonly prompt: LangfusePromptClient,
    private readonly service: LangFuseService,
    readonly session?: Session,
    private readonly traceMessages = false,
  ) {
    this.trace = this.service.langfuse.trace(
      traceMessages
        ? {
            name: this.prompt.name,
            userId: session?.user.email1?.email,
            metadata: {
              alias: session?.alias,
              database_id: session?.database,
              database_name: session?.displayName,
              language: session?.culture,
              user: {
                login: session?.user.id,
                name: session?.user.name,
                email: session?.user.email1?.email,
              },
              workspace_id: session?.workspace,
            },
          }
        : { name: this.prompt.name },
    );
  }

  private readonly trace: LangfuseTraceClient;
  private generation?: LangfuseGenerationClient;

  start(
    url: string,
    configuration: OpenAI.RequestConfiguration,
    messages: OpenAI.GPTMessage[],
  ): void {
    try {
      this.generation = this.trace.generation({
        name: url,
        model: configuration.model,
        modelParameters: Object.entries(configuration).reduce(
          (parameters, [key, value]) => {
            if (key === 'model') {
              return parameters;
            }
            if (typeof value === 'string' || typeof value === 'number') {
              parameters[key] = value;
            }
            return parameters;
          },
          {} as Record<string, string | number>,
        ),
        input: this.traceMessages ? messages : undefined,
        prompt: this.prompt,
      });
    } catch (error) {
      console.error('Error starting Langfuse generation:', error);
      this.generation = undefined;
    }
  }

  end(response: OpenAI.CompletionResponse): void {
    try {
      this.generation?.end({
        output: this.traceMessages ? response?.choices : undefined,
        usageDetails: response?.usage,
      });
    } catch (error) {
      console.error('Error ending Langfuse generation:', error);
      this.generation = undefined;
    }
  }

  error(error: Error): void {
    try {
      this.generation?.end({ output: error });
    } catch (error) {
      console.error('Error ending Langfuse generation:', error);
      this.generation = undefined;
    }
  }
}
